import {BannerCfg} from '../config/BannerCfg';
import {DEFAULT_CONCURRENCY} from '../config/BannerMultiloadCfg';
import {BannerPlaceListenersProxy} from '../listener/BannerPlaceListenersProxy';
import {createLog, getCurrentState} from '../util/BannerCommon';
import {BufferedCommand} from '../util/BufferedCommand';
import {getBannerStatLogger, getErrorOperationWithSuffix} from '../util/BannerStat';
import {getRbSyncData} from '../util/rbUtil';
import type {BannerRequest} from '../BannerRequest';

import type { TBaseParams, IBannerRequestResult, IBannerTransport } from './IBannerTransport';

import {BannerRequestId} from './BannerRequestId';

const log = createLog('BannerTransportJs');

const NAME = 'name';

const HTML = 'html';

const callBackName: string = 'rb_tadq_' + Date.now();

const JSONP_TTL = 5000;

let jsonpCleanupTimeoutId = 0;

let lastRequestId = 1;

let callbackMap: Record<string, RequestCallback> = {};

let runningJSONP: Record<string, number> = {};

let maxRunningJsonp = DEFAULT_CONCURRENCY;

let respNum = 0;

let pending: JSONP[] = [];

let lastJSONPId = 0;

let req2jsonp: Record<string, string> = {};

let req2ReqIdHolder: Record<string, BannerRequestId> = {};

const CLICK_TIMEOUT = 100;

const bannerPlaceListenersProxy = BannerPlaceListenersProxy.getInstance();

let instance: BannerTransport | null = null;

export class BannerTransport implements IBannerTransport {
    private bannerCfg: BannerCfg;
    protected requests: RequestCollector;
    protected flushCommand: BufferedCommand;

    constructor(bannerCfg: BannerCfg) {
        this.bannerCfg = bannerCfg;
        this.requests = new RequestCollector();
        this.flushCommand = new BufferedCommand(
            () => {
                return this.bannerCfg.multiLoad.maxLatency;
            },
            () => {
                return this.bannerCfg.multiLoad.heartbeatDelay;
            },
            this.flush.bind(this)
        );

        maxRunningJsonp = bannerCfg.multiLoad.concurrency;
    }

    protected flush() {
        const baseUrl = this.bannerCfg.multiLoad.url;

        /* Собираем номера всех запрашиваемых слотов */
        let q: string[] = [];
        const pendingObject: { [key: number | string]: BannerRequestId } = this.requests.requests;
        const pendingKeys = Object.getOwnPropertyNames(pendingObject);
        let reqIds: Array<BannerRequestId> = [];
        if (!pendingKeys.length) {
            /* Не требуется отсылать запрос, если нет очереди ожидающих */
            return;
        }
        const logger = getBannerStatLogger();
        pendingKeys.forEach(key => {
            const value: BannerRequestId = pendingObject[key];

            /* Запоминаем список всех сгененированных "номеров" запроса */
            reqIds.push(value);

            /*
             * entry.getKey() — номер слота, он будет возвращаться в JSON как поле 'slot'
             * ? — всё, что после знака есть специальные параметры для слота
             * n — выделяется из специальных параметров для слота, так как значение будет возвращаться в JSON как поле 'name'
             * entry.getValue() — сгенерированный "номер" запроса, он и будет возвращаться и мы можем определить на какой запрос получили ответ RB
             */
            const query = `${key}?n=${value.id}`;
            /*
             * Не забываем кодировать query ( ?n= -> %3Fn%3D ),
             * иначе поломаем корректный парсинг параметров запроса
             */
            q.push("q=" + encodeURIComponent(query));
            /*
             * Логируем вызов РБ
             * param1 — текущая страница
             * param2 — номер слота
             */
            logger.success('ad', getCurrentState(), 'js_adq.' + key);
        });

        log('flush, q', q);

        BannerTransport.appendCookielessUserSyncParameters(this.requests.params);

        /*
         * Формируем итоговый УРЛ, baseUrl + "?callback=" + callBackName + (Площадка ОК) + (соединяем все номера слотов+специальные параметры слотов) + (общие параметры всех запросов в RB (персональная инфа для таргеттинга)
         */
        const url = `${baseUrl}?callback=${callBackName}&_SITEID=163&${q.join('&')}&${BannerTransport.encodeParams(this.requests.params)}`;
        /* Очистка запросов, ожидающих  */
        this.requests.clear();
        log('flush', url, reqIds);
        // Добавление запроса в очередь
        this.addJSONPRequest(url, reqIds);
    }

    /* Что это и зачем см в RbSyncBlock.java , https://confluence.mail.ru/display/RB/Cookieless+User+Sync */
    static appendCookielessUserSyncParameters(params: TBaseParams) {
        const rbSyncData = getRbSyncData((message, error) => {
            log(message, error!);
        });

        Object.assign(params, rbSyncData);
    }

    sendJSONP(container: HTMLElement | null, reqId: BannerRequestId, slot: string, params: TBaseParams) {
        if (!this.requests.addRequest(reqId, slot, params)) {
            this.flushCommand.force();
            this.requests.addRequest(reqId, slot, params);
        }
        this.flushCommand.invoke();
    }

    sendRequest(slot: string, params: TBaseParams, request: BannerRequest) {
        if (!this.bannerCfg.multiLoad.adqEnabled) {
            return;
        }
        const reqId = buildReqId();
        callbackMap[reqId] = new RequestCallback(reqId, request);
        const reqIdHolder = new BannerRequestId(reqId, request.bannerPlace!, slot, request.container)
                                                .setBannerCfg(this.bannerCfg);
        this.sendJSONP(null, reqIdHolder, slot, params);
    }

    addJSONPRequest(jsonpUrl: string, reqIds: Array<BannerRequestId>) {
        if (canSendJSONP()) {
            log('addJSONPRequest now', jsonpUrl, reqIds);
            sendJSONPRequest(jsonpUrl, reqIds);
        } else {
            log('addJSONPRequest queue', jsonpUrl, reqIds);
            this.queueJSONPRequest(jsonpUrl, reqIds);
        }
    }

    queueJSONPRequest(jsonpUrl: string, reqIds: Array<BannerRequestId>) {
        pending.push(new JSONP(jsonpUrl, reqIds));
    }

    sendClick(clickUrl: string, params: TBaseParams) {
        setTimeout(() => {
            const img = new Image();
            img.src = `${clickUrl}?${BannerTransport.encodeParams(params)}`;
        }, CLICK_TIMEOUT);
    }

    static encodeParams(params: TBaseParams) {
        const keys = Object.getOwnPropertyNames(params);
        if (keys.length) {
            const strings: string[] = [];
            keys.forEach(key => {
                const val = params[key];
                if (val !== undefined && val !== null) {
                    strings.push(`${key}=${encodeURIComponent(val)}`);
                }
            });
            return strings.join("&");
        } else {
            return "";
        }
    }

    static appendParam(params: TBaseParams, name: string, value: string|number) {
        if (value !== null) {
            switch (typeof value) {
                case 'string':
                    params[name] = value;
                    break;
                case 'number':
                    params[name] = value + '';
                    break;
                default:
                    break;
            }
        }
        return params;
    }
}

function buildWrapperId(reqId: string): string {
    return "blwrapper_" + reqId;
}

function buildReqId(): string {
    // N.B. adq не может обрабатывать ничего кроме латиницы и цифр, так что никаких прочих символов здесь
    return "nbnr" + (lastRequestId++);
}

function canSendJSONP() {
    return Object.getOwnPropertyNames(runningJSONP).length < maxRunningJsonp;
}

function sendJSONPRequest(jsonpUrl: string, reqIds: Array<BannerRequestId>) {
    log('sendJSONPRequest', jsonpUrl, reqIds);
    const jsonpId = (lastJSONPId++) + '';

    reqIds.forEach(reqId => {
        req2jsonp[reqId.id] = jsonpId;
        req2ReqIdHolder[reqId.id] = reqId;
    });

    setupJSONP(jsonpId, jsonpUrl, reqIds);
}

function handleError(jsonpId: string) {
    for (let key in req2jsonp) {
        if (req2jsonp[key] === jsonpId) {
            handleResponse(key, '');
            break;
        }
    }
}

function setupJSONP(jsonpId: string, jsonpUrl: string, reqIds: Array<BannerRequestId>) {
    let script = document.createElement("script");
    script.type = "text/javascript";
    script.src = `${jsonpUrl}&nn=${respNum}`;
    script.async = true;
    script.id = buildWrapperId(jsonpId);
    script.onerror = function() {
        handleError(jsonpId);
    };

    document.head.appendChild(script);

    runningJSONP[jsonpId] = Date.now();

    scheduleJsonpCleanup();
    log('script tag appended, requests: ' + reqIds.toString());

    const reqIdsWithCallback = reqIds.filter((reqId) => reqId.getBannerCfg().callbacksEnabled);

    if (reqIdsWithCallback.length) {
        reqIdsWithCallback.forEach(reqId => {
            try {
                bannerPlaceListenersProxy.onRequest(reqId);
            } catch (ex) {
                getBannerStatLogger().errorEx(
                    ex as Error,
                    'AdqBannerTransport',
                    'script_on_request',
                    getErrorOperationWithSuffix(reqId.slot)
                );
            }
        });

        const scriptEventListener = new ScriptOnLoadEventListener(script, reqIdsWithCallback);
        script.addEventListener('load', scriptEventListener);
        script.addEventListener('error', scriptEventListener);
    }
}

function handleAdq(jso: Record<string, string>[]) {
    log('handleAdq', jso);
    const logger = getBannerStatLogger();
    if (jso === null) {
        logger.errorEx(
            new Error("ADQ null response"),
            "BannerTransport",
            "adq-callback",
            getErrorOperationWithSuffix()
        );
        return;
    }
    try {
        /* adq декларирует возврат массива */
        const size = jso.length;
        if (size === 0) {
            logger.errorEx(
                new Error("ADQ empty array"),
                "BannerTransport",
                "adq-array",
                getErrorOperationWithSuffix()
            );
            return;
        }
        /* Итерация по массиву */
        for (let idx = 0; idx < size; idx++) {
            const obj: Record<string, string> = jso[idx];
            /* Элементом массива должен быть JSON объект */
            if (obj !== null && typeof obj === 'object') {
                const id = obj[NAME];
                if (!id) {
                    /* Параметр name определяет на какой запрос был получен ответ от RB */
                    logger.errorEx(
                        new Error("ADQ invalid 'name'"),
                        "BannerTransport",
                        "adq-name",
                        getErrorOperationWithSuffix()
                    );
                    // eslint-disable-next-line no-continue
                    continue;
                }

                const reqId = req2ReqIdHolder[id];
                delete req2ReqIdHolder[id];
                const slot = reqId?.slot;
                const response = obj[HTML];
                if (response === undefined) {
                    /* Параметр response содержит ответ от RB, который должен быть использован как содержимое для вставки баннера */
                    logger.errorEx(
                        new Error("ADQ invalid 'html'"),
                        "BannerTransport",
                        "adq-html",
                        getErrorOperationWithSuffix(slot)
                    );
                    // eslint-disable-next-line no-continue
                    continue;
                }

                if (!reqId || !reqId.getBannerCfg().callbacksEnabled) {
                    /* Обрабатываем ответ через существующий механизм */
                    handleResponse(id, response);
                } else {
                    try {
                        const result = handleResponse(id, response);
                        if (reqId != null) {
                            reqId.setResult(result);
                            bannerPlaceListenersProxy.onAdsSuccess(reqId);
                            if (!response || !response.trim()) {
                                bannerPlaceListenersProxy.onNoAds(reqId);
                            }
                        }
                    } catch (ex) {
                        logger.errorEx(
                            ex as Error,
                            'AdqBannerTransport',
                            'handleResponse',
                            getErrorOperationWithSuffix(slot)
                        );
                        if (reqId != null) {
                            bannerPlaceListenersProxy.onScriptError(reqId);
                        }
                    }
                }
            } else {
                logger.errorEx(
                    new Error("ADQ invalid item"),
                    "BannerTransport",
                    "adq-item",
                    getErrorOperationWithSuffix()
                );
            }
        }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
        logger.errorEx(
            new Error("ADQ unexpected"),
            "BannerTransport",
            "adq-unknown",
            getErrorOperationWithSuffix()
        );
    }
}

(window as unknown as Record<string, unknown>)[callBackName] = handleAdq;

function handleResponse(id: string, response: string) {
    log('handleResponse', {
        id: id,
        response: response
    });
    const logger = getBannerStatLogger();
    let slot: string | undefined;
    let result: IBannerRequestResult;
    try {
        const callback = callbackMap[id];
        if (callback) {
            slot = callback.request.slot.getValue();
            result = callback.handleResponse(response);
        }
    } catch (ex) {
        logger.errorEx(
            ex as Error,
            "BannerTransport",
            "callback",
            getErrorOperationWithSuffix(slot)
        );
    }
    try {
        const jsonpId = req2jsonp[id];
        delete req2jsonp[id];
        if (jsonpId) {
            delete runningJSONP[jsonpId];

            const elementId = buildWrapperId(jsonpId);
            const script = document.getElementById(elementId);

            if (script !== null && script.parentNode) {
                script.parentNode.removeChild(script);
                respNum++;
            }
            if (canSendJSONP() && pending.length) {
                const jsonp = pending.shift();
                sendJSONPRequest(jsonp?.url!, jsonp?.reqIds!);
            }
        }
    } catch (ex) {
        logger.errorEx(
            ex as Error,
            "BannerTransport",
            "after_callback",
            getErrorOperationWithSuffix(slot)
        );
    }
    return result;
}

function onJsonpCleanupTimeout() {
    try {
        jsonpCleanupTimeoutId = 0;
        const expired: string[] = [];
        Object.getOwnPropertyNames(runningJSONP).forEach(jsonpId => {
            const elapsedMillis = Date.now() - runningJSONP[jsonpId];
            if (elapsedMillis > JSONP_TTL) {
                expired.push(jsonpId);
            }
        });
        expired.forEach(jsonpId => {
            handleError(jsonpId);
        });
    } catch (e) {
        getBannerStatLogger().errorEx(
            e as Error,
            "BannerTransport",
            "cleanup",
            getErrorOperationWithSuffix()
        );
    }
    // Повторяем проверку до тех пор, пока есть необработанные запросы
    if (Object.getOwnPropertyNames(runningJSONP).length) {
        scheduleJsonpCleanup();
    }
}

function scheduleJsonpCleanup() {
    if (!jsonpCleanupTimeoutId) {
        jsonpCleanupTimeoutId = window.setTimeout(onJsonpCleanupTimeout, JSONP_TTL);
    }
}

// =====================================================
// ============= Вспомогательные классы ================
// =====================================================

class RequestCallback {
    id: string;
    request: BannerRequest;

    constructor(id: string, request: BannerRequest) {
        this.id = id;
        this.request = request;
        this.request.setCancellationListener(this);
    }

    handleResponse(response: string) {
        return this.request.handleResponse(response);
    }

    onCancel() {
        delete callbackMap[this.id];
    }
}

class JSONP {
    url: string;
    reqIds: Array<BannerRequestId>;

    constructor(url: string, reqIds: Array<BannerRequestId>) {
        this.url = url;
        this.reqIds = reqIds;
    }
}

class RequestCollector {
    requests: Record<string, BannerRequestId>;
    params: TBaseParams;

    constructor() {
        this.requests = {};
        this.params = {};
    }

    clear() {
        this.requests = {};
        this.params = {};
    }

    addRequest(reqId: BannerRequestId, slot: string, params: TBaseParams): boolean {
        if (this.requests.hasOwnProperty(slot)) {
            return false;
        } else {
            let intersection = Object.getOwnPropertyNames(this.params);
            intersection = intersection.filter(value => {
                return params.hasOwnProperty(value);
            });

            for (let i = 0; i < intersection.length; i++) {
                const key = intersection[i];
                if (params[key] !== this.params[key]) {
                    return false;
                }
            }

            this.requests[slot] = reqId;

            Object.getOwnPropertyNames(params).forEach(key => {
                this.params[key] = params[key];
            });

            return true;
        }
    }
}

class ScriptOnLoadEventListener {
    script: HTMLScriptElement;
    reqIds: Array<BannerRequestId>;

    constructor(script: HTMLScriptElement, reqIds: Array<BannerRequestId>) {
        this.script = script;
        this.reqIds = reqIds;
    }

    handleEvent(e: Event) {
        log('script on \'' + e.type + '\' event handling, requests: ' + this.reqIds.toString());
        const logger = getBannerStatLogger();
        if (e.type === 'load') {
            this.reqIds.forEach(reqId => {
                try {
                    bannerPlaceListenersProxy.onLoadSuccess(reqId);
                } catch (ex) {
                    logger.errorEx(
                        ex as Error,
                        "AdqBannerTransport",
                        "script_on_load",
                        getErrorOperationWithSuffix(reqId.slot)
                    );
                }
            });
        } else if (e.type === 'error') {
            this.reqIds.forEach(reqId => {
                try {
                    bannerPlaceListenersProxy.onLoadError(reqId);
                } catch (ex) {
                    logger.errorEx(
                        ex as Error,
                        "AdqBannerTransport",
                        "script_on_error",
                        getErrorOperationWithSuffix(reqId.slot)
                    );
                }
            });
        }
        this.script.removeEventListener('load', this);
        this.script.removeEventListener('error', this);
    }
}
